/* ===========================================================================
   ZYREX — animations.js
   ---------------------------------------------------------------------------
   The whole motion engine for the theme, in one deferred file, no dependencies.

   Every other file in this theme participates in motion by adding attributes
   only. Nothing else should ever have to write an IntersectionObserver, a
   scroll listener or a requestAnimationFrame loop.

   Attribute API
   -------------
   data-reveal="up|down|left|right|fade|scale|blur|rise-rotate"   (default: up)
     data-reveal-delay="0..1200"        milliseconds
     data-reveal-duration="200..1400"   milliseconds
     data-reveal-once="false"           re-hides when it leaves the viewport
   data-reveal-group                     auto-staggers descendant reveals
     data-reveal-stagger="80"           milliseconds between them
   data-split="words|chars"              wraps parts in .split__i with --i
   data-magnetic  data-magnetic-strength="0.3"
   data-tilt      data-tilt-max="7"  data-tilt-glare
   data-parallax  data-parallax-speed="0.18"  data-parallax-rotate  data-parallax-max
   data-counter   data-counter-to  data-counter-prefix  data-counter-suffix
                  data-counter-decimals  data-counter-duration="1600"
   data-marquee   data-marquee-speed="34"  data-marquee-reverse  data-marquee-pause-hover
   data-scroll-progress="page|element"   writes --progress: 0..1
   data-hover-video
   data-cursor="hover|hide"

   Root state, written before paint
   -------------------------------
   html.js-anim       always, so animations.css may apply its hidden states
   html.no-anim       theme setting off OR prefers-reduced-motion (live)
   html.fine-pointer  a hoverable, precise pointer is present (live)
   body.is-scrolled   the page has scrolled past 12px

   Public API
   ----------
   window.ZAnim.refresh(root)   re-scan a subtree (Theme Editor, AJAX content)
   window.ZAnim.reveal(el)      force one element (or selector) to its end state

   Every entry point is defensive: missing nodes, missing settings, missing
   browser features and hostile markup must never produce a console error.
   =========================================================================== */

(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var win = window;
  var doc = document;
  var root = doc.documentElement;

  /* -----------------------------------------------------------------------
     Constants
  ----------------------------------------------------------------------- */

  var CLASS_JS = 'js-anim';
  var CLASS_NO_ANIM = 'no-anim';
  var CLASS_FINE = 'fine-pointer';
  var CLASS_IN = 'is-in';
  var CLASS_SCROLLED = 'is-scrolled';
  var CLASS_TILTING = 'is-tilting';

  var SCROLLED_THRESHOLD = 12;
  var DEFAULT_STAGGER = 80;
  var DEFAULT_SPLIT_STAGGER = 90;
  var DEFAULT_REVEAL_DURATION = 600;
  var DEFAULT_COUNTER_DURATION = 1600;
  var DEFAULT_MARQUEE_SPEED = 34;
  var DEFAULT_MAGNETIC_STRENGTH = 0.3;
  var DEFAULT_TILT_MAX = 7;
  var DEFAULT_TILT_PERSPECTIVE = 900;
  var DEFAULT_PARALLAX_SPEED = 0.18;

  var REVEAL_VARIANTS = {
    up: 1,
    down: 1,
    left: 1,
    right: 1,
    fade: 1,
    scale: 1,
    blur: 1,
    'rise-rotate': 1
  };

  var hasIO = typeof win.IntersectionObserver === 'function';
  var hasMO = typeof win.MutationObserver === 'function';
  var hasRO = typeof win.ResizeObserver === 'function';

  /* -----------------------------------------------------------------------
     Tiny helpers

     Deliberately not reusing window.ZUtils: theme.js loads first in practice,
     but this file must still work if it does not.
  ----------------------------------------------------------------------- */

  function noop() {}

  function now() {
    return win.performance && typeof win.performance.now === 'function'
      ? win.performance.now()
      : Date.now();
  }

  function raf(fn) {
    return typeof win.requestAnimationFrame === 'function'
      ? win.requestAnimationFrame(fn)
      : win.setTimeout(fn, 16);
  }

  function cancelRaf(id) {
    if (!id) return;
    if (typeof win.cancelAnimationFrame === 'function') win.cancelAnimationFrame(id);
    else win.clearTimeout(id);
  }

  function clamp(value, min, max) {
    if (!isFinite(value)) return min;
    return value < min ? min : value > max ? max : value;
  }

  function toNumber(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : fallback;
  }

  function isElement(node) {
    return !!node && node.nodeType === 1;
  }

  function floatAttr(el, name, fallback) {
    if (!isElement(el) || !el.hasAttribute(name)) return fallback;
    return toNumber(el.getAttribute(name), fallback);
  }

  function intAttr(el, name, fallback) {
    var value = floatAttr(el, name, fallback);
    return isFinite(value) ? Math.round(value) : fallback;
  }

  function debounce(fn, wait) {
    var timer = 0;
    return function () {
      var args = arguments;
      var self = this;
      win.clearTimeout(timer);
      timer = win.setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /**
   * Runs `fn` for `selector` inside `scope`, and for `scope` itself when it
   * matches. Theme Editor events hand us the section element, which is often
   * the very node carrying the attribute.
   */
  function each(scope, selector, fn) {
    if (!scope) return;
    try {
      if (isElement(scope) && typeof scope.matches === 'function' && scope.matches(selector)) {
        fn(scope);
      }
      if (typeof scope.querySelectorAll !== 'function') return;
      var list = scope.querySelectorAll(selector);
      for (var i = 0; i < list.length; i++) fn(list[i]);
    } catch (error) {
      /* A detached node or an unsupported selector must never break the page. */
    }
  }

  function resolveScope(scope) {
    if (!scope) return doc;
    if (typeof scope === 'string') {
      try {
        return doc.querySelector(scope) || null;
      } catch (error) {
        return null;
      }
    }
    if (scope.nodeType === 1 || scope.nodeType === 9 || scope.nodeType === 11) return scope;
    if (scope.target && scope.target.nodeType) return scope.target;
    return doc;
  }

  function onMediaChange(mq, handler) {
    if (!mq) return;
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler);
    else if (typeof mq.addListener === 'function') mq.addListener(handler);
  }

  /* -----------------------------------------------------------------------
     Settings

     `window.Zyrex` is written by layout/theme.liquid. It may legitimately be
     absent (an isolated preview, a stripped template), so every field falls
     back to a sensible default rather than throwing.
  ----------------------------------------------------------------------- */

  var settings = {
    enabled: true,
    speed: 1,
    speedProvided: false,
    revealDefault: 'up',
    magnetic: true,
    tilt: true,
    marqueeSpeed: DEFAULT_MARQUEE_SPEED,
    blobs: true,
    scrollProgress: true
  };

  function readSettings() {
    var config = (win.Zyrex && win.Zyrex.settings) || {};

    settings.enabled = config.animEnabled !== false;
    settings.speedProvided = config.animSpeed !== undefined && config.animSpeed !== null;
    settings.speed = clamp(toNumber(config.animSpeed, 1), 0.4, 2);
    settings.magnetic = config.animMagnetic !== false;
    settings.tilt = config.animTilt !== false;
    settings.blobs = config.animBlobs !== false;
    settings.scrollProgress = config.animScrollProgress !== false;
    settings.marqueeSpeed = clamp(
      toNumber(config.animMarqueeSpeed, DEFAULT_MARQUEE_SPEED),
      4,
      300
    );

    var variant = String(config.animRevealDefault || 'up').toLowerCase();
    settings.revealDefault = REVEAL_VARIANTS[variant] ? variant : 'up';
  }

  readSettings();

  /* -----------------------------------------------------------------------
     Root motion state
  ----------------------------------------------------------------------- */

  var mqReduce = typeof win.matchMedia === 'function'
    ? win.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var mqFine = typeof win.matchMedia === 'function'
    ? win.matchMedia('(hover: hover) and (pointer: fine)')
    : null;

  function prefersReducedMotion() {
    return !!(mqReduce && mqReduce.matches);
  }

  function isFinePointer() {
    return !!(mqFine && mqFine.matches);
  }

  function motionOff() {
    return !settings.enabled || prefersReducedMotion();
  }

  function setRootClass(name, on) {
    if (!root || !root.classList) return;
    if (on) root.classList.add(name);
    else root.classList.remove(name);
  }

  function applyRootState() {
    setRootClass(CLASS_JS, true);
    setRootClass(CLASS_NO_ANIM, motionOff());
    setRootClass(CLASS_FINE, isFinePointer());

    /* Only write the scale when the theme actually told us one, otherwise
       base.css keeps ownership of the token. */
    if (settings.speedProvided && root.style) {
      root.style.setProperty('--anim-scale', String(settings.speed));
    }
  }

  applyRootState();

  /* -----------------------------------------------------------------------
     Teardown registry

     Theme Editor section unloads must release listeners and rAF loops, so
     every interactive module registers how to undo itself.
  ----------------------------------------------------------------------- */

  var teardowns = [];

  function registerTeardown(el, fn) {
    if (!isElement(el) || typeof fn !== 'function') return;
    teardowns.push({ el: el, fn: fn });
  }

  function runTeardowns(scope) {
    var node = resolveScope(scope);
    var kept = [];
    for (var i = 0; i < teardowns.length; i++) {
      var entry = teardowns[i];
      var inside = false;
      try {
        inside = !node || node === doc || node === entry.el ||
          (node.contains && node.contains(entry.el));
      } catch (error) {
        inside = false;
      }
      if (inside) {
        try {
          entry.fn();
        } catch (error) {
          /* Never let one broken teardown block the rest. */
        }
      } else {
        kept.push(entry);
      }
    }
    teardowns = kept;
  }

  /* =======================================================================
     REVEAL
     ======================================================================= */

  var revealObserver = null;

  function ensureRevealObserver() {
    if (revealObserver || !hasIO) return revealObserver;
    revealObserver = new win.IntersectionObserver(handleRevealEntries, {
      root: null,
      /* Fire a touch before the element reaches the very bottom edge so the
         transition is already underway when it becomes properly visible. */
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.01
    });
    return revealObserver;
  }

  function handleRevealEntries(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var el = entry.target;
      if (!isElement(el)) continue;

      var once = el.getAttribute('data-reveal-once') !== 'false';

      if (entry.isIntersecting) {
        showReveal(el);
        if (once && revealObserver) {
          try {
            revealObserver.unobserve(el);
          } catch (error) {
            /* already gone */
          }
        }
      } else if (!once) {
        hideReveal(el);
      }
    }
  }

  function showReveal(el) {
    if (!isElement(el) || !el.classList) return;
    if (el.classList.contains(CLASS_IN)) return;
    el.classList.add(CLASS_IN);
    fireCountersInside(el);
  }

  function hideReveal(el) {
    if (!isElement(el) || !el.classList) return;
    if (motionOff()) return;
    el.classList.remove(CLASS_IN);
  }

  /**
   * Normalises one reveal target: a known variant, and the two custom
   * properties animations.css reads. Returns true the first time only.
   */
  function prepareReveal(el) {
    if (!isElement(el)) return false;
    if (el.hasAttribute('data-reveal-ready')) return false;

    var variant = String(el.getAttribute('data-reveal') || '').trim().toLowerCase();
    if (!REVEAL_VARIANTS[variant]) {
      variant = settings.revealDefault;
      el.setAttribute('data-reveal', variant);
    }

    if (el.hasAttribute('data-reveal-duration')) {
      var duration = clamp(
        intAttr(el, 'data-reveal-duration', DEFAULT_REVEAL_DURATION),
        60,
        4000
      );
      el.style.setProperty('--reveal-duration', duration + 'ms');
    }

    if (el.hasAttribute('data-reveal-delay')) {
      var delay = clamp(intAttr(el, 'data-reveal-delay', 0), 0, 4000);
      el.style.setProperty('--reveal-delay', delay + 'ms');
    }

    el.setAttribute('data-reveal-ready', '');
    return true;
  }

  function observeReveal(el) {
    if (!isElement(el)) return;

    if (motionOff() || !hasIO) {
      showReveal(el);
      return;
    }

    var observer = ensureRevealObserver();
    if (!observer) {
      showReveal(el);
      return;
    }

    try {
      observer.observe(el);
    } catch (error) {
      showReveal(el);
    }
  }

  /** The group a reveal belongs to, ignoring the element itself. */
  function owningGroup(el) {
    if (!isElement(el) || !el.parentNode || typeof el.parentNode.closest !== 'function') {
      return null;
    }
    try {
      return el.parentNode.closest('[data-reveal-group]');
    } catch (error) {
      return null;
    }
  }

  /**
   * Walks a group in document order and writes an increasing --reveal-delay on
   * every descendant reveal that has no explicit delay of its own. Items with
   * an explicit delay still consume a slot so the visual rhythm survives.
   */
  function applyGroupStagger(group) {
    if (!isElement(group)) return;

    var step = clamp(intAttr(group, 'data-reveal-stagger', DEFAULT_STAGGER), 0, 600);
    var base = clamp(intAttr(group, 'data-reveal-group-delay', 0), 0, 2000);
    var items;

    try {
      items = group.querySelectorAll('[data-reveal]');
    } catch (error) {
      return;
    }

    var index = 0;
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (owningGroup(el) !== group) continue;

      if (!el.hasAttribute('data-reveal-delay') && !el.hasAttribute('data-reveal-staggered')) {
        el.style.setProperty('--reveal-delay', base + index * step + 'ms');
        el.setAttribute('data-reveal-staggered', '');
      }
      index++;
    }
  }

  /** Force everything inside a subtree to its finished state. */
  function revealAll(scope) {
    each(resolveScope(scope), '[data-reveal]', function (el) {
      prepareReveal(el);
      showReveal(el);
    });
    each(resolveScope(scope), '[data-counter]', function (el) {
      runCounter(el, true);
    });
  }

  /* =======================================================================
     SPLIT TEXT
     ======================================================================= */

  function splitIntoWords(text) {
    var parts = String(text).split(/(\s+)/);
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i] === '') continue;
      out.push({ value: parts[i], space: /^\s+$/.test(parts[i]) });
    }
    return out;
  }

  function splitIntoChars(text) {
    var source = String(text);
    var chars = typeof Array.from === 'function' ? Array.from(source) : source.split('');
    var out = [];
    for (var i = 0; i < chars.length; i++) {
      out.push({ value: chars[i], space: /\s/.test(chars[i]) });
    }
    return out;
  }

  /**
   * Wraps the text inside `el` in per-word or per-character spans.
   *
   * The parent keeps an aria-label with the original wording and every part is
   * aria-hidden, so a screen reader announces one clean string instead of a
   * stream of fragments. Existing child elements survive: only text nodes are
   * replaced, so a link or an <em> inside a heading still works.
   */
  function initSplit(el) {
    if (!isElement(el)) return;
    if (el.hasAttribute('data-split-ready')) return;

    var mode = String(el.getAttribute('data-split') || 'words').trim().toLowerCase();
    if (mode !== 'chars') mode = 'words';

    var label = String(el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!label) {
      el.setAttribute('data-split-ready', '');
      return;
    }

    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', label);

    var textNodes = [];
    try {
      var walker = doc.createTreeWalker(el, win.NodeFilter ? win.NodeFilter.SHOW_TEXT : 4, null, false);
      var node;
      while ((node = walker.nextNode())) textNodes.push(node);
    } catch (error) {
      el.setAttribute('data-split-ready', '');
      return;
    }

    var stagger = clamp(intAttr(el, 'data-split-stagger', DEFAULT_SPLIT_STAGGER), 0, 400);
    el.style.setProperty('--split-stagger', stagger + 'ms');

    var index = 0;

    for (var i = 0; i < textNodes.length; i++) {
      var textNode = textNodes[i];
      var text = textNode.nodeValue;
      if (!text || !text.trim()) continue;
      if (!textNode.parentNode) continue;
      /* Do not re-split something a previous pass already produced. */
      if (textNode.parentNode.classList && textNode.parentNode.classList.contains('split__i')) {
        continue;
      }

      var parts = mode === 'chars' ? splitIntoChars(text) : splitIntoWords(text);
      var fragment = doc.createDocumentFragment();

      for (var p = 0; p < parts.length; p++) {
        var part = parts[p];
        if (part.space) {
          /* Plain text nodes keep normal wrapping behaviour between parts. */
          fragment.appendChild(doc.createTextNode(part.value));
          continue;
        }
        var span = doc.createElement('span');
        span.className = 'split__i';
        span.setAttribute('aria-hidden', 'true');
        span.style.setProperty('--i', String(index));
        span.appendChild(doc.createTextNode(part.value));
        fragment.appendChild(span);
        index++;
      }

      textNode.parentNode.replaceChild(fragment, textNode);
    }

    /* The observer keys off data-reveal, so guarantee there is one. */
    if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'fade');

    el.setAttribute('data-split-ready', '');
  }

  /* =======================================================================
     MAGNETIC
     ======================================================================= */

  function initMagnetic(el) {
    if (!isElement(el) || el.getAttribute('data-magnetic-ready') === 'true') return;
    if (motionOff() || !settings.magnetic || !isFinePointer()) return;

    el.setAttribute('data-magnetic-ready', 'true');

    var strength = clamp(floatAttr(el, 'data-magnetic-strength', DEFAULT_MAGNETIC_STRENGTH), 0, 1);
    var targetX = 0;
    var targetY = 0;
    var currentX = 0;
    var currentY = 0;
    var frame = 0;

    function paint() {
      if (Math.abs(currentX) < 0.05 && Math.abs(currentY) < 0.05) {
        el.style.transform = '';
        return;
      }
      el.style.transform =
        'translate3d(' + currentX.toFixed(2) + 'px,' + currentY.toFixed(2) + 'px,0)';
    }

    /* A light critically-damped lerp: fast enough to feel attached to the
       cursor, slow enough that the release reads as a spring, not a snap. */
    function tick() {
      currentX += (targetX - currentX) * 0.18;
      currentY += (targetY - currentY) * 0.18;

      var settled =
        Math.abs(targetX - currentX) < 0.08 && Math.abs(targetY - currentY) < 0.08;

      if (settled) {
        currentX = targetX;
        currentY = targetY;
        paint();
        frame = 0;
        return;
      }

      paint();
      frame = raf(tick);
    }

    function start() {
      if (!frame) frame = raf(tick);
    }

    function onMove(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) return;
      targetX = (event.clientX - (box.left + box.width / 2)) * strength;
      targetY = (event.clientY - (box.top + box.height / 2)) * strength;
      start();
    }

    function release() {
      targetX = 0;
      targetY = 0;
      start();
    }

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('pointerup', release);
    el.addEventListener('focusout', release);

    registerTeardown(el, function () {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', release);
      el.removeEventListener('pointercancel', release);
      el.removeEventListener('pointerup', release);
      el.removeEventListener('focusout', release);
      cancelRaf(frame);
      frame = 0;
      el.style.transform = '';
      el.removeAttribute('data-magnetic-ready');
    });
  }

  /* =======================================================================
     TILT
     ======================================================================= */

  function initTilt(el) {
    if (!isElement(el) || el.getAttribute('data-tilt-ready') === 'true') return;
    if (motionOff() || !settings.tilt || !isFinePointer()) return;

    el.setAttribute('data-tilt-ready', 'true');

    var max = clamp(floatAttr(el, 'data-tilt-max', DEFAULT_TILT_MAX), 0, 25);
    var perspective = clamp(
      floatAttr(el, 'data-tilt-perspective', DEFAULT_TILT_PERSPECTIVE),
      200,
      4000
    );

    var rotateX = 0;
    var rotateY = 0;
    var frame = 0;

    function paint() {
      frame = 0;
      el.style.transform =
        'perspective(' + perspective + 'px) rotateX(' + rotateX.toFixed(2) +
        'deg) rotateY(' + rotateY.toFixed(2) + 'deg)';
    }

    function onMove(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) return;

      var px = (event.clientX - box.left) / box.width;
      var py = (event.clientY - box.top) / box.height;

      rotateY = (px - 0.5) * max * 2;
      rotateX = (0.5 - py) * max * 2;

      el.style.setProperty('--glare-x', (px * 100).toFixed(2) + '%');
      el.style.setProperty('--glare-y', (py * 100).toFixed(2) + '%');

      el.classList.add(CLASS_TILTING);
      if (!frame) frame = raf(paint);
    }

    function release() {
      cancelRaf(frame);
      frame = 0;
      rotateX = 0;
      rotateY = 0;
      el.classList.remove(CLASS_TILTING);
      el.style.transform = '';
      el.style.setProperty('--glare-x', '50%');
      el.style.setProperty('--glare-y', '50%');
    }

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('focusout', release);

    registerTeardown(el, function () {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', release);
      el.removeEventListener('pointercancel', release);
      el.removeEventListener('focusout', release);
      release();
      el.removeAttribute('data-tilt-ready');
    });
  }

  /* =======================================================================
     PARALLAX  (one shared scroll loop for every registered element)
     ======================================================================= */

  var parallaxItems = [];

  function initParallax(el) {
    if (!isElement(el) || el.getAttribute('data-parallax-ready') === 'true') return;
    if (motionOff()) return;

    el.setAttribute('data-parallax-ready', 'true');

    var item = {
      el: el,
      speed: clamp(floatAttr(el, 'data-parallax-speed', DEFAULT_PARALLAX_SPEED), -1.5, 1.5),
      rotate: clamp(floatAttr(el, 'data-parallax-rotate', 0), -45, 45),
      scale: clamp(floatAttr(el, 'data-parallax-scale', 0), -0.6, 0.6),
      max: clamp(floatAttr(el, 'data-parallax-max', 160), 0, 600),
      axis: String(el.getAttribute('data-parallax-axis') || 'y').toLowerCase() === 'x' ? 'x' : 'y'
    };

    parallaxItems.push(item);
    updateParallax();
    startScrollLoop();

    registerTeardown(el, function () {
      for (var i = parallaxItems.length - 1; i >= 0; i--) {
        if (parallaxItems[i].el === el) parallaxItems.splice(i, 1);
      }
      el.style.transform = '';
      el.removeAttribute('data-parallax-ready');
    });
  }

  /**
   * Transform-only, measured from the element's distance to the centre of the
   * viewport. Nothing here touches top/left, so it stays on the compositor.
   */
  function updateParallax() {
    if (!parallaxItems.length) return;

    var viewportHeight = win.innerHeight || root.clientHeight || 0;
    if (!viewportHeight) return;

    for (var i = 0; i < parallaxItems.length; i++) {
      var item = parallaxItems[i];
      var el = item.el;
      if (!el || !el.isConnected) continue;

      var box;
      try {
        box = el.getBoundingClientRect();
      } catch (error) {
        continue;
      }
      if (!box.height && !box.width) continue;

      /* Distance of the element's centre from the viewport centre, negative
         above and positive below. */
      var delta = box.top + box.height / 2 - viewportHeight / 2;
      var travel = clamp(-delta * item.speed, -item.max, item.max);
      var progress = clamp(delta / viewportHeight, -1, 1);

      var transform = item.axis === 'x'
        ? 'translate3d(' + travel.toFixed(2) + 'px,0,0)'
        : 'translate3d(0,' + travel.toFixed(2) + 'px,0)';

      if (item.rotate) {
        transform += ' rotate(' + (progress * item.rotate * -1).toFixed(2) + 'deg)';
      }
      if (item.scale) {
        transform += ' scale(' + (1 + progress * item.scale * -1).toFixed(4) + ')';
      }

      el.style.transform = transform;
    }
  }

  /* =======================================================================
     COUNTERS
     ======================================================================= */

  var counterObserver = null;

  function ensureCounterObserver() {
    if (counterObserver || !hasIO) return counterObserver;
    counterObserver = new win.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          runCounter(entries[i].target, false);
          try {
            counterObserver.unobserve(entries[i].target);
          } catch (error) {
            /* already gone */
          }
        }
      },
      { root: null, rootMargin: '0px 0px -5% 0px', threshold: 0.4 }
    );
    return counterObserver;
  }

  function makeFormatter(decimals) {
    var locale = root.getAttribute('lang') || 'en';
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    } catch (error) {
      return {
        format: function (value) {
          return Number(value).toFixed(decimals);
        }
      };
    }
  }

  function initCounter(el) {
    if (!isElement(el) || el.getAttribute('data-counter-ready') === 'true') return;
    el.setAttribute('data-counter-ready', 'true');

    var rawTarget = el.getAttribute('data-counter-to');
    if (rawTarget === null || rawTarget === '') rawTarget = el.textContent || '0';

    var cleaned = String(rawTarget).replace(/[^0-9.\-]/g, '');
    var target = toNumber(cleaned, 0);

    var inferred = cleaned.indexOf('.') >= 0 ? cleaned.split('.')[1].length : 0;
    var decimals = clamp(intAttr(el, 'data-counter-decimals', inferred), 0, 4);

    var duration = clamp(
      intAttr(el, 'data-counter-duration', DEFAULT_COUNTER_DURATION),
      0,
      10000
    ) * settings.speed;

    var formatter = makeFormatter(decimals);
    var prefix = el.getAttribute('data-counter-prefix') || '';
    var suffix = el.getAttribute('data-counter-suffix') || '';

    var state = {
      el: el,
      to: target,
      duration: duration,
      done: false,
      frame: 0,
      paint: function (value) {
        el.textContent = prefix + formatter.format(value) + suffix;
      }
    };

    el.__zCounter = state;

    /* Someone who never sees the animation must still see the number. */
    if (motionOff() || !hasIO || duration <= 0) {
      state.done = true;
      state.paint(target);
      return;
    }

    state.paint(0);

    var observer = ensureCounterObserver();
    if (!observer) {
      runCounter(el, true);
      return;
    }

    try {
      observer.observe(el);
    } catch (error) {
      runCounter(el, true);
    }

    registerTeardown(el, function () {
      cancelRaf(state.frame);
      state.frame = 0;
      el.removeAttribute('data-counter-ready');
      el.__zCounter = null;
    });
  }

  /** easeOutExpo — the reference's count-up curve. */
  function easeOutExpo(t) {
    return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function runCounter(el, immediate) {
    if (!isElement(el)) return;
    var state = el.__zCounter;
    if (!state) return;

    /* `immediate` must win even when a count is already in flight — the Theme
       Editor calls it to snap a selected section to its finished state, and a
       half-counted number reads as broken. */
    if (immediate || motionOff() || state.duration <= 0) {
      cancelRaf(state.frame);
      state.frame = 0;
      state.done = true;
      state.paint(state.to);
      return;
    }

    if (state.done) return;
    state.done = true;

    var start = now();

    function step() {
      var elapsed = now() - start;
      var t = clamp(elapsed / state.duration, 0, 1);
      state.paint(state.to * easeOutExpo(t));

      if (t < 1) {
        state.frame = raf(step);
      } else {
        state.frame = 0;
        state.paint(state.to);
      }
    }

    state.frame = raf(step);
  }

  /** Counters nested inside a reveal start with it rather than on their own. */
  function fireCountersInside(el) {
    if (!isElement(el)) return;
    each(el, '[data-counter]', function (node) {
      runCounter(node, false);
    });
  }

  /* =======================================================================
     MARQUEE
     ======================================================================= */

  function initMarquee(el) {
    if (!isElement(el) || el.getAttribute('data-marquee-init') === 'true') return;
    el.setAttribute('data-marquee-init', 'true');

    if (el.classList) el.classList.add('marquee');

    var track =
      el.querySelector('[data-marquee-track]') || el.querySelector('.marquee__track');

    /* Markup may hand us a bare list of items — wrap them so the animation has
       something to translate. */
    if (!track) {
      track = doc.createElement('div');
      track.className = 'marquee__track';
      track.setAttribute('data-marquee-track', '');
      while (el.firstChild) track.appendChild(el.firstChild);
      el.appendChild(track);
    }

    if (track.classList) track.classList.add('marquee__track');

    /* The visible loop is decoration; sections render an accessible copy of the
       same words separately. */
    if (!el.hasAttribute('aria-hidden')) el.setAttribute('aria-hidden', 'true');

    /* The pristine item list is kept on the node, never in an attribute, so a
       rebuild always starts from the original markup instead of compounding
       the copies made by the previous pass. */
    if (typeof track.__zSeed !== 'string') track.__zSeed = track.innerHTML;

    var speed = clamp(
      floatAttr(el, 'data-marquee-speed', settings.marqueeSpeed),
      4,
      300
    );
    el.style.setProperty('--marquee-duration', (speed * settings.speed).toFixed(2) + 's');

    function build() {
      if (!el.isConnected) return;

      track.innerHTML = track.__zSeed || '';

      if (motionOff()) {
        el.removeAttribute('data-marquee-ready');
        return;
      }

      var containerWidth = el.clientWidth || 0;
      var guard = 0;

      /* Repeat the source items until one pass already covers the container,
         then mirror the whole track so translateX(-50%) lands seamlessly. The
         guard stops a zero-width or empty seed from looping forever. */
      while (containerWidth > 0 && track.scrollWidth < containerWidth * 1.05 && guard < 40) {
        var before = track.scrollWidth;
        track.insertAdjacentHTML('beforeend', track.__zSeed || '');
        guard++;
        if (track.scrollWidth <= before) break;
      }

      track.insertAdjacentHTML('beforeend', track.innerHTML);
      el.setAttribute('data-marquee-ready', '');
    }

    el.__zMarqueeBuild = build;
    build();

    var resizeObserver = null;
    if (hasRO) {
      var rebuild = debounce(build, 180);
      resizeObserver = new win.ResizeObserver(function () {
        rebuild();
      });
      try {
        resizeObserver.observe(el);
      } catch (error) {
        resizeObserver = null;
      }
    }

    registerTeardown(el, function () {
      if (resizeObserver) {
        try {
          resizeObserver.disconnect();
        } catch (error) {
          /* nothing to do */
        }
      }
      el.__zMarqueeBuild = null;
      el.removeAttribute('data-marquee-init');
      el.removeAttribute('data-marquee-ready');
    });
  }

  function rebuildMarquees(scope) {
    each(resolveScope(scope), '[data-marquee]', function (el) {
      if (typeof el.__zMarqueeBuild === 'function') el.__zMarqueeBuild();
    });
  }

  /* =======================================================================
     SCROLL PROGRESS
     ======================================================================= */

  var progressItems = [];

  function initScrollProgress(el) {
    if (!isElement(el) || el.getAttribute('data-scroll-progress-ready') === 'true') return;

    var mode = String(el.getAttribute('data-scroll-progress') || 'page').trim().toLowerCase();
    if (mode !== 'element') mode = 'page';

    if (!settings.scrollProgress) {
      el.style.setProperty('--progress', '0');
      return;
    }

    el.setAttribute('data-scroll-progress-ready', 'true');
    progressItems.push({ el: el, mode: mode });

    updateProgress();
    startScrollLoop();

    registerTeardown(el, function () {
      for (var i = progressItems.length - 1; i >= 0; i--) {
        if (progressItems[i].el === el) progressItems.splice(i, 1);
      }
      el.removeAttribute('data-scroll-progress-ready');
    });
  }

  function updateProgress() {
    if (!progressItems.length) return;

    var viewportHeight = win.innerHeight || root.clientHeight || 0;
    var scrolled = win.pageYOffset || root.scrollTop || 0;
    var documentHeight = Math.max(
      doc.body ? doc.body.scrollHeight : 0,
      root ? root.scrollHeight : 0,
      viewportHeight
    );
    var pageMax = Math.max(documentHeight - viewportHeight, 1);
    var pageProgress = clamp(scrolled / pageMax, 0, 1);

    for (var i = 0; i < progressItems.length; i++) {
      var item = progressItems[i];
      if (!item.el || !item.el.isConnected) continue;

      var value = pageProgress;

      if (item.mode === 'element') {
        var box;
        try {
          box = item.el.getBoundingClientRect();
        } catch (error) {
          continue;
        }
        var span = box.height + viewportHeight || 1;
        value = clamp((viewportHeight - box.top) / span, 0, 1);
      }

      item.el.style.setProperty('--progress', value.toFixed(4));
    }
  }

  /* =======================================================================
     HOVER VIDEO
     ======================================================================= */

  function initHoverVideo(el) {
    if (!isElement(el) || el.getAttribute('data-hover-video-ready') === 'true') return;

    var video = null;
    try {
      video = el.matches('video') ? el : el.querySelector('video');
    } catch (error) {
      video = null;
    }
    if (!video) return;

    el.setAttribute('data-hover-video-ready', 'true');

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'metadata');

    function play() {
      if (motionOff()) return;
      try {
        var promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(noop);
      } catch (error) {
        /* Autoplay policies vary; a refused play is not an error worth logging. */
      }
    }

    function stop() {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (error) {
        /* nothing to do */
      }
    }

    el.addEventListener('pointerenter', play);
    el.addEventListener('pointerleave', stop);
    el.addEventListener('focusin', play);
    el.addEventListener('focusout', stop);

    registerTeardown(el, function () {
      el.removeEventListener('pointerenter', play);
      el.removeEventListener('pointerleave', stop);
      el.removeEventListener('focusin', play);
      el.removeEventListener('focusout', stop);
      stop();
      el.removeAttribute('data-hover-video-ready');
    });
  }

  /* =======================================================================
     CURSOR HINTS  (optional nicety, fine pointers only)
     ======================================================================= */

  function initCursor(el) {
    if (!isElement(el) || el.getAttribute('data-cursor-ready') === 'true') return;
    if (!isFinePointer()) return;

    el.setAttribute('data-cursor-ready', 'true');

    var mode = String(el.getAttribute('data-cursor') || 'hover').toLowerCase();

    function enter() {
      setRootClass('cursor-' + (mode === 'hide' ? 'hide' : 'hover'), true);
    }

    function leave() {
      setRootClass('cursor-hide', false);
      setRootClass('cursor-hover', false);
    }

    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);

    registerTeardown(el, function () {
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('pointerleave', leave);
      leave();
      el.removeAttribute('data-cursor-ready');
    });
  }

  /* =======================================================================
     SHARED SCROLL LOOP
     ======================================================================= */

  var scrollLoopStarted = false;
  var scrollTicking = false;
  var lastScrolled = null;

  function updateScrollState() {
    var scrolled = win.pageYOffset || root.scrollTop || 0;
    var isScrolled = scrolled > SCROLLED_THRESHOLD;

    if (isScrolled === lastScrolled) return;
    lastScrolled = isScrolled;

    if (doc.body && doc.body.classList) {
      if (isScrolled) doc.body.classList.add(CLASS_SCROLLED);
      else doc.body.classList.remove(CLASS_SCROLLED);
    }
  }

  function onScrollFrame() {
    scrollTicking = false;
    updateScrollState();
    updateParallax();
    updateProgress();
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    raf(onScrollFrame);
  }

  function onResize() {
    lastScrolled = null;
    onScroll();
  }

  function startScrollLoop() {
    if (scrollLoopStarted) return;
    scrollLoopStarted = true;

    var options = supportsPassive() ? { passive: true } : false;
    win.addEventListener('scroll', onScroll, options);
    win.addEventListener('resize', onResize, options);
    win.addEventListener('orientationchange', onResize, options);
  }

  var passiveChecked = false;
  var passiveSupported = false;

  function supportsPassive() {
    if (passiveChecked) return passiveSupported;
    passiveChecked = true;
    try {
      var options = Object.defineProperty({}, 'passive', {
        get: function () {
          passiveSupported = true;
          return true;
        }
      });
      win.addEventListener('zyrex-passive-test', noop, options);
      win.removeEventListener('zyrex-passive-test', noop, options);
    } catch (error) {
      passiveSupported = false;
    }
    return passiveSupported;
  }

  /* =======================================================================
     REFRESH — the single entry point every other module uses
     ======================================================================= */

  function refresh(scope) {
    var node = resolveScope(scope);
    if (!node) return;

    /* Order matters: splitting can add data-reveal, and group staggering must
       see the final set of reveals before any of them are observed. */
    each(node, '[data-split]', initSplit);
    each(node, '[data-reveal-group]', applyGroupStagger);
    each(node, '[data-reveal]', function (el) {
      if (prepareReveal(el)) observeReveal(el);
    });

    each(node, '[data-counter]', initCounter);
    each(node, '[data-marquee]', initMarquee);
    each(node, '[data-magnetic]', initMagnetic);
    each(node, '[data-tilt]', initTilt);
    each(node, '[data-parallax]', initParallax);
    each(node, '[data-scroll-progress]', initScrollProgress);
    each(node, '[data-hover-video]', initHoverVideo);
    each(node, '[data-cursor]', initCursor);
  }

  /* =======================================================================
     MUTATION OBSERVER — keeps AJAX and Section Rendering content alive
     ======================================================================= */

  var mutationObserver = null;
  var pendingNodes = [];

  var flushMutations = debounce(function () {
    if (!pendingNodes.length) return;

    var queue = pendingNodes;
    pendingNodes = [];

    stopMutationObserver();
    for (var i = 0; i < queue.length; i++) {
      if (queue[i] && queue[i].isConnected) refresh(queue[i]);
    }
    startMutationObserver();
  }, 90);

  function handleMutations(records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      if (!added || !added.length) continue;

      for (var n = 0; n < added.length; n++) {
        var node = added[n];
        if (!isElement(node)) continue;
        if (pendingNodes.length > 200) {
          /* A very large injection: fall back to one whole-document pass. */
          pendingNodes = [doc];
          flushMutations();
          return;
        }
        pendingNodes.push(node);
      }
    }
    if (pendingNodes.length) flushMutations();
  }

  function startMutationObserver() {
    if (!hasMO || !doc.body) return;
    if (!mutationObserver) mutationObserver = new win.MutationObserver(handleMutations);
    try {
      mutationObserver.observe(doc.body, { childList: true, subtree: true });
    } catch (error) {
      mutationObserver = null;
    }
  }

  function stopMutationObserver() {
    if (!mutationObserver) return;
    try {
      mutationObserver.takeRecords();
      mutationObserver.disconnect();
    } catch (error) {
      /* nothing to do */
    }
  }

  /* =======================================================================
     THEME EDITOR
     ======================================================================= */

  function bindEditorEvents() {
    doc.addEventListener('shopify:section:load', function (event) {
      var target = event.target || doc;
      refresh(target);
      rebuildMarquees(target);
    });

    doc.addEventListener('shopify:section:unload', function (event) {
      var target = event.target;
      if (!target) return;
      each(target, '[data-reveal]', function (el) {
        if (revealObserver) {
          try {
            revealObserver.unobserve(el);
          } catch (error) {
            /* already gone */
          }
        }
      });
      runTeardowns(target);
    });

    doc.addEventListener('shopify:section:reorder', function () {
      refresh(doc);
      rebuildMarquees(doc);
      onScroll();
    });

    /* Selecting a section or block scrolls the editor to it; anything still
       hidden at that moment would read as a bug. */
    doc.addEventListener('shopify:section:select', function (event) {
      refresh(event.target);
      revealAll(event.target);
    });

    doc.addEventListener('shopify:block:select', function (event) {
      refresh(event.target);
      revealAll(event.target);
    });

    doc.addEventListener('shopify:section:deselect', function (event) {
      refresh(event.target);
    });

    doc.addEventListener('shopify:block:deselect', function (event) {
      refresh(event.target);
    });
  }

  /* =======================================================================
     MOTION PREFERENCE CHANGES
     ======================================================================= */

  function releaseInteractiveModules() {
    each(doc, '[data-magnetic]', function (el) {
      el.style.transform = '';
    });
    each(doc, '[data-tilt]', function (el) {
      el.style.transform = '';
      if (el.classList) el.classList.remove(CLASS_TILTING);
    });
    each(doc, '[data-parallax]', function (el) {
      el.style.transform = '';
    });
    parallaxItems.length = 0;
  }

  function handleMotionPreferenceChange() {
    applyRootState();

    if (motionOff()) {
      /* Turning motion off must leave a finished, static page behind. */
      releaseInteractiveModules();
      revealAll(doc);
      rebuildMarquees(doc);
      return;
    }

    /* Turning motion back on: re-arm the modules that opted out earlier.
       Already-revealed elements keep their finished state, which is correct —
       re-hiding content someone has already read would be hostile. */
    refresh(doc);
    rebuildMarquees(doc);
    onScroll();
  }

  onMediaChange(mqReduce, handleMotionPreferenceChange);
  onMediaChange(mqFine, function () {
    applyRootState();
    if (!motionOff()) refresh(doc);
  });

  /* =======================================================================
     PUBLIC API
     ======================================================================= */

  win.ZAnim = {
    version: '1.0.0',

    /** Re-scan a subtree. Accepts an element, a document, a selector or null. */
    refresh: function (scope) {
      refresh(scope);
    },

    /** Push one element (or selector) straight to its finished state. */
    reveal: function (target) {
      var el = typeof target === 'string' ? resolveScope(target) : target;
      if (!isElement(el)) return;
      prepareReveal(el);
      showReveal(el);
    },

    /** Finish every reveal and counter inside a subtree. */
    revealAll: function (scope) {
      revealAll(scope);
    },

    /** Recompute marquee duplication, e.g. after a font or layout change. */
    refreshMarquees: function (scope) {
      rebuildMarquees(scope);
    },

    /** Recompute parallax and progress right now. */
    update: function () {
      updateScrollState();
      updateParallax();
      updateProgress();
    },

    /** True when motion is suppressed by the theme setting or the OS. */
    isMotionOff: function () {
      return motionOff();
    },

    /** Re-read window.Zyrex.settings, e.g. after a Theme Editor change. */
    reloadSettings: function () {
      readSettings();
      handleMotionPreferenceChange();
    },

    settings: settings
  };

  /* =======================================================================
     BOOT
     ======================================================================= */

  var booted = false;

  function boot() {
    if (booted) return;
    if (!doc.body) return;
    booted = true;

    applyRootState();
    refresh(doc);
    startScrollLoop();
    startMutationObserver();
    bindEditorEvents();

    updateScrollState();
    updateParallax();
    updateProgress();
  }

  if (doc.body) {
    boot();
  } else if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    raf(boot);
  }

  /* Web fonts and late images change the measurements marquees and parallax
     depend on, so take one more pass once everything has settled. */
  win.addEventListener('load', function () {
    boot();
    rebuildMarquees(doc);
    onScroll();
  });

  if (doc.fonts && typeof doc.fonts.ready === 'object' && doc.fonts.ready.then) {
    doc.fonts.ready.then(function () {
      rebuildMarquees(doc);
      onScroll();
    }).catch(noop);
  }

  /* A page restored from the back/forward cache keeps its old scroll offset but
     not always its class state. */
  win.addEventListener('pageshow', function (event) {
    if (!event || !event.persisted) return;
    lastScrolled = null;
    onScroll();
  });
})();
